import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, ArrowLeft, Zap, Building2, Loader2 } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Pricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/admin/public/pricing`);
      if (response.data && response.data.regions) {
        const defaultRegion = response.data.regions.find(r => r.region === response.data.default_region) 
          || response.data.regions[0];
        if (defaultRegion) {
          setPricing({
            currency: defaultRegion.currency,
            currencySymbol: defaultRegion.currency === 'EUR' ? '€' : 
                           defaultRegion.currency === 'USD' ? '$' : 
                           defaultRegion.currency === 'GBP' ? '£' : 
                           defaultRegion.currency === 'INR' ? '₹' : defaultRegion.currency,
            monthly_price: defaultRegion.monthly_price,
            yearly_price: defaultRegion.yearly_price
          });
        }
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      // Fallback to defaults
      setPricing({
        currency: 'EUR',
        currencySymbol: '€',
        monthly_price: 9.99,
        yearly_price: 99.99
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate dynamic savings
  const monthlyPrice = pricing?.monthly_price || 9.99;
  const yearlyPrice = pricing?.yearly_price || 99.99;
  const currencySymbol = pricing?.currencySymbol || '€';
  const annualSavings = (monthlyPrice * 12) - yearlyPrice;

  // Same features for both plans
  const allFeatures = [
    t('pricing.feature1'),
    t('pricing.feature2'),
    t('pricing.feature3'),
    t('pricing.feature4'),
    t('pricing.feature5'),
    t('pricing.feature6'),
  ];

  const plans = [
    {
      name: t('pricing.monthlyPlan'),
      price: `${currencySymbol}${monthlyPrice.toFixed(2)}`,
      period: t('pricing.perMonth'),
      description: t('pricing.monthlyDesc'),
      icon: Zap,
      features: allFeatures,
      buttonText: t('pricing.startTrial'),
      popular: false,
      savings: null,
    },
    {
      name: t('pricing.yearlyPlan'),
      price: `${currencySymbol}${yearlyPrice.toFixed(2)}`,
      period: t('pricing.perYear'),
      description: t('pricing.yearlyDesc'),
      icon: Building2,
      features: allFeatures,
      buttonText: t('pricing.startTrial'),
      popular: true,
      savings: annualSavings > 0 ? `Save ${currencySymbol}${annualSavings.toFixed(2)}/year` : null,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.back')}
            </Button>
            <div className="flex flex-col items-start cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/tourpilot-logo.png" 
                alt="TourPilot.biz" 
                className="h-14 max-w-[220px] object-contain"
              />
              <span className="text-xs text-muted-foreground mt-1 hidden sm:block whitespace-nowrap">
                {t('landing.tagline')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button variant="ghost" onClick={() => navigate('/login')}>
              {t('common.login')}
            </Button>
            <Button onClick={() => navigate('/login')}>
              {t('landing.getStarted')}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4 text-center">
          <Badge variant="secondary" className="mb-4">{t('pricing.badge')}</Badge>
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold text-foreground mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </div>
      </section>

      {/* Free Trial Banner */}
      <section className="py-8 bg-primary/10">
        <div className="container mx-auto px-4 text-center">
          <p className="text-lg font-semibold text-primary">
            🎉 {t('pricing.first2MonthsFree')}
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative ${plan.popular ? 'border-primary shadow-lg scale-105' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground">
                      {t('pricing.bestValue')}
                    </Badge>
                  </div>
                )}
                {plan.savings && (
                  <div className="absolute -top-3 right-4">
                    <Badge className="bg-green-600 text-white hover:bg-green-700">
                      🎉 {plan.savings}
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <plan.icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-2xl font-heading">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-extrabold text-foreground">{plan.price}</span>
                    {plan.period && (
                      <span className="text-muted-foreground ml-1">{plan.period}</span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full" 
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => navigate('/login')}
                  >
                    {plan.buttonText}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-heading font-extrabold text-center mb-12">
            {t('pricing.faqTitle')}
          </h2>
          <div className="space-y-6">
            <div className="bg-card rounded-lg p-6 border border-border">
              <h3 className="font-semibold text-foreground mb-2">{t('pricing.faq1Question')}</h3>
              <p className="text-muted-foreground text-sm">{t('pricing.faq1Answer')}</p>
            </div>
            <div className="bg-card rounded-lg p-6 border border-border">
              <h3 className="font-semibold text-foreground mb-2">{t('pricing.faq2Question')}</h3>
              <p className="text-muted-foreground text-sm">{t('pricing.faq2Answer')}</p>
            </div>
            <div className="bg-card rounded-lg p-6 border border-border">
              <h3 className="font-semibold text-foreground mb-2">{t('pricing.faq3Question')}</h3>
              <p className="text-muted-foreground text-sm">{t('pricing.faq3Answer')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border bg-card">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="items-start">
              <img 
                src="/tourpilot-logo.png" 
                alt="TourPilot.biz" 
                className="h-14 max-w-[220px] object-contain mb-2"
              />
              <p className="text-xs text-muted-foreground mb-3 whitespace-nowrap text-left">
                {t('landing.tagline')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('footer.description')}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">{t('footer.product')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/#features" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.features')}</a></li>
                <li><a href="/pricing" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.pricing')}</a></li>
                <li><a href="/support" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.support')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">{t('footer.company')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.privacyPolicy')}</a></li>
                <li><a href="/terms-of-service" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.termsOfService')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">{t('footer.contact')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>2DS Finland Oy</li>
                <li>Espoo, Finland</li>
                <li>sales@2ds.fi</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
